import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight, ArrowLeft } from "lucide-react";

function GoogleButton({ label }) {
  const handleClick = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button
      data-testid="google-auth-btn"
      onClick={handleClick}
      type="button"
      className="w-full inline-flex items-center justify-center gap-3 py-3 rounded-full border border-rule bg-white hover:bg-paper-2 transition-colors text-[14px] font-medium text-ink"
    >
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5c-6.4 0-11.5-5.2-11.5-11.5S17.6 12.5 24 12.5c3 0 5.7 1.1 7.7 2.9l5.7-5.7C33.8 6.6 29.1 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.4-.3-3.5z"/>
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 12.5 24 12.5c3 0 5.7 1.1 7.7 2.9l5.7-5.7C33.8 6.6 29.1 4.5 24 4.5 16.3 4.5 9.7 8.9 6.3 14.7z"/>
        <path fill="#4CAF50" d="M24 43.5c5 0 9.6-1.9 13-5.1l-6-5.1c-2 1.4-4.4 2.2-7 2.2-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C9.5 39 16.2 43.5 24 43.5z"/>
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.4-2.4 4.4-4.3 5.8l6 5.1c-.4.4 6.5-4.8 6.5-14.9 0-1.2-.1-2.4-.3-3.5z"/>
      </svg>
      {label}
    </button>
  );
}

const PERKS = [
  "NAVEGA por nuestra plataforma.",
  "3 MINUTOS son suficientes para ver el PODER",
  "Enamórate de esta GRAN HERRAMIENTA",
];

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [acceptedLegal, setAcceptedLegal] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (password.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres");
      return;
    }
    if (!acceptedLegal) {
      toast.error("Debes aceptar los Términos del Servicio y el Aviso de Privacidad");
      return;
    }
    setSubmitting(true);
    try {
      const acceptedAt = new Date().toISOString();
      const user = await register(email, password, name, {
        terms_accepted_at: acceptedAt,
        privacy_accepted_at: acceptedAt,
      });
      toast.success(`Cuenta creada. ¡Bienvenido, ${user.name.split(" ")[0]}!`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No fue posible crear la cuenta");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-paper">
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <Link
            to="/"
            data-testid="back-to-home"
            className="inline-flex items-center gap-1.5 text-[12px] text-ink-soft hover:text-forest mb-6 transition-colors"
          >
            <ArrowLeft size={13} /> Volver al inicio
          </Link>
          <Link to="/" data-testid="brand-link" className="inline-flex items-baseline gap-0.5">
            <span className="font-serif italic text-2xl text-copper">una</span><span className="font-serif text-2xl text-ink">TESIS</span>
          </Link>
          <h1 className="mt-10 font-serif text-3xl text-ink leading-tight">
            Crea tu cuenta gratis
          </h1>
          <p className="mt-2 text-sm text-ink-soft">
            Sin tarjetas. Configura tu universidad en 60 segundos.
          </p>

          <div className="mt-8">
            <GoogleButton label="Registrarme con Google" />
            <p className="mt-2.5 text-[10.5px] text-ink-soft/85 leading-relaxed text-center">
              Al continuar con Google aceptas los{" "}
              <Link to="/terminos" target="_blank" className="text-forest underline underline-offset-2">Términos</Link>{" "}
              y el{" "}
              <Link to="/privacidad" target="_blank" className="text-forest underline underline-offset-2">Aviso de Privacidad</Link>.
            </p>
          </div>

          <div className="my-7 flex items-center gap-3 text-[11px] tracking-[0.18em] uppercase text-ink-soft/70">
            <span className="flex-1 h-px bg-rule" />
            o con tu correo
            <span className="flex-1 h-px bg-rule" />
          </div>

          <form onSubmit={onSubmit} className="flex flex-col gap-4" data-testid="signup-form">
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] tracking-[0.18em] uppercase text-ink-soft font-medium">Nombre</span>
              <input
                data-testid="signup-name"
                type="text"
                required
                minLength={2}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Tu nombre completo"
                className="px-4 py-3 rounded-lg border border-rule bg-white text-[14px] outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] tracking-[0.18em] uppercase text-ink-soft font-medium">Correo</span>
              <input
                data-testid="signup-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tunombre@universidad.mx"
                className="px-4 py-3 rounded-lg border border-rule bg-white text-[14px] outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] tracking-[0.18em] uppercase text-ink-soft font-medium">Contraseña</span>
              <div className="relative">
                <input
                  data-testid="signup-password"
                  type={showPwd ? "text" : "password"}
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                  className="w-full px-4 py-3 rounded-lg border border-rule bg-white text-[14px] outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition pr-11"
                />
                <button
                  type="button"
                  aria-label="Mostrar contraseña"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-soft hover:text-ink"
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </label>

            <label
              data-testid="signup-legal-row"
              className="mt-1 flex items-start gap-3 rounded-lg border border-rule bg-paper-2/40 px-3.5 py-3 cursor-pointer hover:border-forest/30 transition-colors"
            >
              <input
                data-testid="signup-legal-checkbox"
                type="checkbox"
                checked={acceptedLegal}
                onChange={(e) => setAcceptedLegal(e.target.checked)}
                required
                className="mt-0.5 h-4 w-4 shrink-0 accent-forest cursor-pointer"
              />
              <span className="text-[12.5px] text-ink-soft leading-relaxed">
                He leído y acepto los{" "}
                <Link
                  to="/terminos"
                  target="_blank"
                  data-testid="signup-link-terms"
                  className="text-forest font-medium underline underline-offset-2 hover:text-copper"
                >
                  Términos del Servicio
                </Link>{" "}
                y el{" "}
                <Link
                  to="/privacidad"
                  target="_blank"
                  data-testid="signup-link-privacy"
                  className="text-forest font-medium underline underline-offset-2 hover:text-copper"
                >
                  Aviso de Privacidad
                </Link>
                , incluyendo el deslinde de responsabilidad sobre el resultado académico al presentar mi tesis.
              </span>
            </label>

            <button
              data-testid="signup-submit"
              type="submit"
              disabled={submitting || !acceptedLegal}
              className="mt-2 group inline-flex items-center justify-center gap-2 py-3.5 rounded-full text-[14px] font-medium text-paper bg-forest hover:bg-forest-dark transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-sm hover:shadow-md hover:-translate-y-[1px]"
            >
              {submitting ? "Creando cuenta…" : "Crear cuenta gratis"}
              {!submitting && <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />}
            </button>
          </form>

          <p className="mt-8 text-sm text-ink-soft text-center">
            ¿Ya tienes cuenta?{" "}
            <Link to="/login" data-testid="link-to-login" className="text-forest font-medium hover:text-copper transition-colors">
              Iniciar sesión
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:flex items-center justify-center bg-[#F3F1EC] relative overflow-hidden">
        <div className="max-w-md px-12">
          <p className="text-[11px] tracking-[0.28em] uppercase text-copper font-medium">
            Freemium
          </p>
          <ol className="mt-8 flex flex-col gap-5">
            {PERKS.map((p, i) => (
              <li key={p} className="flex items-start gap-4">
                <span className="mt-0.5 grid place-items-center h-8 w-8 shrink-0 rounded-full bg-forest text-paper font-serif text-[15px]">
                  {i + 1}
                </span>
                <span className="font-serif text-xl text-ink leading-snug">{p}</span>
              </li>
            ))}
          </ol>
          <div className="mt-10 flex justify-center">
            <a
              href="#signup-form"
              data-testid="freemium-cta"
              onClick={(e) => {
                e.preventDefault();
                document
                  .querySelector('[data-testid="signup-email"]')
                  ?.focus({ preventScroll: false });
              }}
              className="group inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full text-[14px] font-medium text-paper bg-forest hover:bg-forest-dark transition-all shadow-sm hover:shadow-md hover:-translate-y-[1px] tracking-[0.14em] uppercase"
            >
              Entra gratis
            </a>
          </div>
        </div>
        <div aria-hidden className="absolute -bottom-32 -right-32 h-[440px] w-[440px] rounded-full bg-[#E9E2D2]/60 blur-3xl" />
      </div>
    </div>
  );
}
